import i18n, { type i18n as I18n } from 'i18next';
import { initReactI18next } from 'react-i18next';
import type { Locale } from '@et/schema';
import { lexiconResources } from './lexicon-resources.js';
import en from './locales/en.json' with { type: 'json' };

export const LOCALES: readonly Locale[] = ['en', 'es', 'pt-PT'];
export const CANONICAL: Locale = 'en';

/**
 * [05 §9] — só a língua canónica entra no arranque.
 *
 * Enviar as três a toda a gente é mandar dois terços de um ficheiro que
 * ninguém vai ler, e o orçamento de bundle existe exactamente para impedir
 * esse tipo de generosidade. A língua escolhida entra a seguir, por `import()`,
 * e o `fallbackLng` cobre os milissegundos entre uma coisa e outra — o que se
 * vê nesse intervalo é inglês correcto, não chaves cruas.
 */
const LOADERS: Record<string, () => Promise<{ default: unknown }>> = {
  // Sem `with { type: 'json' }` nos dinamicos, de proposito: o atributo faz o
  // browser exigir `application/json`, e o bundler serve JSON como modulo JS.
  // O import falhava em silencio e a app ficava em ingles — que e a forma de
  // falhar mais dificil de notar, porque parece uma tradução em falta.
  es: () => import('./locales/es.json'),
  'pt-PT': () => import('./locales/pt-PT.json'),
};

/**
 * Os recursos **síncronos**: `en` mais o léxico das três línguas.
 *
 * O léxico fica todo porque é derivado das tabelas do schema e já lá está —
 * separá-lo pouparia bytes que não existem.
 */
export const resources = Object.fromEntries(
  LOCALES.map((l) => [l, { ui: l === CANONICAL ? en : {}, lexicon: lexiconResources(l) }]),
);

export function createI18n(locale: Locale = CANONICAL): I18n {
  const instance = i18n.createInstance();
  void instance.use(initReactI18next).init({
    lng: locale,
    fallbackLng: CANONICAL,
    defaultNS: 'ui',
    ns: ['ui', 'lexicon'],
    resources,
    interpolation: { escapeValue: false },
    returnNull: false,
  });
  const load = LOADERS[locale];
  if (load !== undefined) {
    void load().then((mod) => {
      instance.addResourceBundle(locale, 'ui', mod.default, true, true);
      // Sem isto, o que já está no ecrã fica em inglês até à próxima
      // renderização — e "a app abriu em inglês" é um bug que se lembra.
      void instance.changeLanguage(locale);
    });
  }
  return instance;
}

/** Para testes e para o portão I22: as três línguas, carregadas de propósito. */
export async function allChromeResources(): Promise<Record<string, unknown>> {
  const out: Record<string, unknown> = { en };
  for (const [locale, load] of Object.entries(LOADERS)) out[locale] = (await load()).default;
  return out;
}

export { lexiconResources };
export { chromeResources } from './chrome.js';
